import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Play } from 'lucide-react';
import { toast } from 'sonner';
import { usePermission } from '@/Hooks/usePermission';
import { useCurrency } from '@/Hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ErrorState, LoadingState } from '@/components/ui/states';
import {
  screenCurrency,
  useAnalyticsMeta,
  useBranchOptions,
  type AnalyticsMeta,
} from '../analytics';
import {
  blankDefinition,
  REPORT_OUTPUT_FORMATS,
  type ReportDefinitionInput,
  type ReportOutputFormat,
  type ReportResult,
  type SavedReport,
} from '../reporting-types';
import {
  createSavedReport,
  exportDefinition,
  exportSavedReport,
  previewReport,
  saveBlob,
  updateSavedReport,
} from '../reportingApi';
import { apiMessage } from '../lib';
import { DefinitionEditor } from './DefinitionEditor';
import { ReportPreview } from './ReportPreview';
import { SavedReportsPanel } from './SavedReportsPanel';

/**
 * ADR-0013's builder screen. Every option on it — measures, dimensions, which
 * pairs with which, whether THIS caller may pick it — comes from the same
 * `GET /analytics/meta` the dashboard's ad-hoc `QueryBuilder` reads; there is
 * no second, hand-typed list of measure or dimension names anywhere in this
 * file. `previewReport` and `exportDefinition`/`exportSavedReport` run the
 * SAME compiler the fixed dashboard panels do (ADR-0013 §2.1), so a number
 * built here can never disagree with one built there.
 */
export default function ReportBuilderTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { code: fallbackCurrency } = useCurrency();
  const meta = useAnalyticsMeta();
  const { branches } = useBranchOptions();

  const [selected, setSelected] = useState<SavedReport | null>(null);
  const [definition, setDefinition] = useState<ReportDefinitionInput | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isShared, setIsShared] = useState(true);
  const [savedToken, setSavedToken] = useState(0);

  const [result, setResult] = useState<ReportResult | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [format, setFormat] = useState<ReportOutputFormat>('XLSX');
  const [exporting, setExporting] = useState(false);

  const catalogue: AnalyticsMeta | null = meta.data;

  const resetToBlank = () => {
    setSelected(null);
    setDefinition(catalogue ? blankDefinition(catalogue.limits.defaultRows) : null);
    setName('');
    setDescription('');
    setIsShared(true);
    setResult(null);
    setRunError(null);
  };

  // Seed the first draft once the registry is known.
  useEffect(() => {
    if (catalogue && definition === null && !selected) {
      setDefinition(blankDefinition(catalogue.limits.defaultRows));
    }
  }, [catalogue, definition, selected]);

  const openReport = (report: SavedReport) => {
    setSelected(report);
    setDefinition(report.definition);
    setName(report.name);
    setDescription(report.description ?? '');
    setIsShared(report.isShared);
    setResult(null);
    setRunError(null);
  };

  const dirty =
    selected !== null &&
    definition !== null &&
    (JSON.stringify(definition) !== JSON.stringify(selected.definition) ||
      name !== selected.name ||
      description !== (selected.description ?? '') ||
      isShared !== selected.isShared);

  const branchFilterValues = definition?.filters.branch ?? [];
  const singleBranchFilter = branchFilterValues.length === 1 ? branchFilterValues[0] : '';
  const { code: currency, mixed, codes } = screenCurrency(branches, singleBranchFilter, fallbackCurrency);

  const canPreview = !!definition && definition.measures.length > 0;

  const runPreview = async () => {
    if (!definition || !canPreview) return;
    setRunning(true);
    setRunError(null);
    try {
      const next = await previewReport(definition);
      setResult(next);
    } catch (error) {
      setResult(null);
      setRunError(apiMessage(error, t('reporting.errors.previewFailed')));
    } finally {
      setRunning(false);
    }
  };

  const save = async () => {
    if (!definition || !name.trim()) return;
    setSaving(true);
    try {
      if (selected) {
        const updated = await updateSavedReport(selected.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          definition,
          isShared,
        });
        setSelected(updated);
        toast.success(t('reporting.saved.updated', { name: updated.name }));
      } else {
        const created = await createSavedReport({
          name: name.trim(),
          description: description.trim() || undefined,
          definition,
          isShared,
        });
        setSelected(created);
        toast.success(t('reporting.saved.created', { name: created.name }));
      }
      setSavedToken((token) => token + 1);
    } catch (error) {
      toast.error(apiMessage(error, t('reporting.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const runExport = async () => {
    if (!definition || !canPreview) return;
    setExporting(true);
    try {
      const file =
        selected && !dirty
          ? await exportSavedReport(selected.id, format, selected.name)
          : await exportDefinition(definition, format, name.trim() || 'report');
      saveBlob(file);
    } catch (error) {
      toast.error(apiMessage(error, t('reporting.errors.exportFailed')));
    } finally {
      setExporting(false);
    }
  };

  if (meta.error) return <ErrorState message={meta.error} onRetry={meta.reload} />;
  if (!catalogue || !definition) return <LoadingState label={t('common.loading')} />;

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <div className="lg:h-[calc(100vh-16rem)]">
        <SavedReportsPanel
          selectedId={selected?.id ?? null}
          onSelect={openReport}
          onNew={resetToBlank}
          reloadToken={savedToken}
        />
      </div>

      <div className="space-y-6 rounded-xl border bg-surface p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('reporting.saved.name')} required>
            <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} />
          </Field>
          <Field label={t('reporting.saved.description')}>
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={1}
              maxLength={500}
            />
          </Field>
        </div>

        <div className="flex items-center gap-2">
          <Switch checked={isShared} onChange={setIsShared} aria-label={t('reporting.saved.shared')} />
          <span className="text-sm">
            {isShared ? t('reporting.saved.shared') : t('reporting.saved.private')}
          </span>
        </div>

        {mixed && (
          <p
            role="status"
            className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
          >
            {t('reports.currency.mixedNotice', { codes: codes.join(', ') })}
          </p>
        )}

        <DefinitionEditor meta={catalogue} value={definition} onChange={setDefinition} branches={branches} />

        <div className="flex flex-wrap items-center gap-2 border-t pt-4">
          <Button onClick={runPreview} disabled={!canPreview || running}>
            <Icon as={Play} aria-hidden />
            {t('reporting.builder.runPreview')}
          </Button>

          {can('reports.manage') && (
            <Button
              variant="secondary"
              onClick={save}
              disabled={saving || !name.trim() || (!!selected && !dirty)}
            >
              {selected ? t('reporting.saved.save') : t('reporting.saved.saveAs')}
            </Button>
          )}

          {can('reports.export') && (
            <div className="ms-auto flex items-center gap-2">
              <Select
                value={format}
                onChange={(event) => setFormat(event.target.value as ReportOutputFormat)}
                aria-label={t('reporting.export.format')}
                className="w-28"
              >
                {REPORT_OUTPUT_FORMATS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
              <Button variant="outline" onClick={runExport} disabled={!canPreview || exporting}>
                <Icon as={Download} aria-hidden />
                {t('reports.export')}
              </Button>
            </div>
          )}
        </div>

        {!canPreview ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-ink-muted">
            {t('reports.builder.pickMeasureHint')}
          </p>
        ) : (
          <ReportPreview
            result={result}
            loading={running}
            error={runError}
            currency={currency}
            meta={catalogue}
            onRetry={runPreview}
          />
        )}
      </div>
    </div>
  );
}
